import { Link, useLocation } from 'react-router-dom';
import { Home, Briefcase, MoreHorizontal } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface TabProps {
  to?: string;
  icon: ReactNode;
  label: string;
  matchPaths?: string[];
  comingSoon?: boolean;
  onClick?: () => void;
}

const Tab = ({ to, icon, label, matchPaths, comingSoon, onClick }: TabProps) => {
  const location = useLocation();
  const isActive = !comingSoon && to && (matchPaths ?? [to]).some((p) =>
    p === '/' ? location.pathname === '/' : location.pathname.startsWith(p),
  );

  const baseClasses =
    'flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-[10px] font-body font-medium transition-colors';
  const stateClasses = comingSoon
    ? 'text-dark-invirtus/30'
    : isActive
      ? 'text-dark-invirtus'
      : 'text-dark-invirtus/60 active:text-dark-invirtus';

  const content = (
    <>
      <span className={`flex h-5 w-5 items-center justify-center [&_svg]:h-5 [&_svg]:w-5`}>
        {icon}
      </span>
      <span className="leading-none">{label}</span>
    </>
  );

  if (comingSoon || !to) {
    return (
      <div className={`${baseClasses} ${stateClasses}`} aria-disabled>
        {content}
      </div>
    );
  }

  return (
    <Link to={to} onClick={onClick} className={`${baseClasses} ${stateClasses}`}>
      {content}
    </Link>
  );
};

interface MobileBottomTabProps {
  onOpenMore: () => void;
}

export const MobileBottomTab = ({ onOpenMore }: MobileBottomTabProps) => {
  const { t } = useTranslation('common');
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 flex h-14 items-stretch border-t border-light-lila/40 bg-light/95 backdrop-blur"
      aria-label="Mobile primary navigation"
    >
      <Tab
        to="/home"
        icon={<Home />}
        label={t('nav.home')}
        matchPaths={['/home']}
      />
      <Tab
        to="/jobs"
        icon={<Briefcase />}
        label={t('nav.jobs')}
        matchPaths={['/dashboard', '/jobs', '/job']}
      />
      <button
        onClick={onOpenMore}
        className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-[10px] font-body font-medium text-dark-invirtus/60 active:text-dark-invirtus transition-colors"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <MoreHorizontal className="h-5 w-5" />
        </span>
        <span className="leading-none">{t('nav.more')}</span>
      </button>
    </nav>
  );
};
